import { useEffect, useState } from "react";
import {
  VEHICLE_ACCESS_METHODS,
  VEHICLE_SOURCE_STATUSES,
  VEHICLE_SOURCE_TYPES,
} from "../../../../constants/vehicleSourceConstants";

const EMPTY_FORM = {
  sourceName: "",
  sourceKey: "",
  sourceType: "MARKETPLACE",
  baseUrl: "",
  city: "",
  department: "",
  country: "Colombia",
  coverageArea: "",
  accessMethod: "DISCOVERY_PENDING",
  sourceStatus: "DISCOVERED",
  priority: 100,
  isActive: false,
  notes: "",
};

function sourceToForm(source) {
  if (!source) return { ...EMPTY_FORM };
  return {
    sourceName: source.sourceName || "",
    sourceKey: source.sourceKey || "",
    sourceType: source.sourceType || "MARKETPLACE",
    baseUrl: source.baseUrl || "",
    city: source.city || "",
    department: source.department || "",
    country: source.country || "Colombia",
    coverageArea: source.coverageArea || "",
    accessMethod: source.accessMethod || "DISCOVERY_PENDING",
    sourceStatus: source.sourceStatus || "DISCOVERED",
    priority: source.priority ?? 100,
    isActive: Boolean(source.isActive),
    notes: source.notes || "",
  };
}

const VehicleSourceFormModal = ({ mode, source, onClose, onSave, canManage }) => {
  const [form, setForm] = useState(() => sourceToForm(source));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setForm(sourceToForm(source));
    setErrorMessage("");
  }, [source, mode]);

  const isView = mode === "view";
  const isCreate = mode === "create";
  const readOnly = isView || !canManage;

  const handleChange = (field) => (event) => {
    const value =
      field === "isActive"
        ? event.target.checked
        : field === "priority"
          ? Number(event.target.value)
          : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (readOnly) return;

    setSaving(true);
    setErrorMessage("");
    try {
      await onSave({
        sourceName: form.sourceName.trim(),
        sourceKey: form.sourceKey.trim().toUpperCase(),
        sourceType: form.sourceType,
        baseUrl: form.baseUrl.trim() || null,
        city: form.city.trim() || null,
        department: form.department.trim() || null,
        country: form.country.trim() || "Colombia",
        coverageArea: form.coverageArea.trim() || null,
        accessMethod: form.accessMethod,
        sourceStatus: form.sourceStatus,
        priority: Number(form.priority),
        isActive: form.isActive,
        notes: form.notes.trim() || null,
      });
      onClose();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save vehicle source.");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "create"
      ? "Add vehicle source"
      : mode === "edit"
        ? "Edit vehicle source"
        : "Vehicle source details";

  return (
    <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-labelledby="vehicle-source-form-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-modal__header">
          <h2 id="vehicle-source-form-title">{title}</h2>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </header>

        <form className="admin-modal__body admin-form-grid" onSubmit={handleSubmit}>
          {errorMessage ? <p className="admin-form-error">{errorMessage}</p> : null}

          <label>
            Source name *
            <input
              type="text"
              value={form.sourceName}
              onChange={handleChange("sourceName")}
              required
              disabled={readOnly}
            />
          </label>

          <label>
            Source key *
            <input
              type="text"
              value={form.sourceKey}
              onChange={handleChange("sourceKey")}
              required
              disabled={readOnly || !isCreate}
              placeholder="CARROYA"
            />
          </label>

          <label>
            Source type *
            <select
              value={form.sourceType}
              onChange={handleChange("sourceType")}
              disabled={readOnly}
            >
              {VEHICLE_SOURCE_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Base URL
            <input
              type="url"
              value={form.baseUrl}
              onChange={handleChange("baseUrl")}
              disabled={readOnly}
              placeholder="https://"
            />
          </label>

          <label>
            City
            <input type="text" value={form.city} onChange={handleChange("city")} disabled={readOnly} />
          </label>

          <label>
            Department
            <input
              type="text"
              value={form.department}
              onChange={handleChange("department")}
              disabled={readOnly}
            />
          </label>

          <label>
            Country
            <input
              type="text"
              value={form.country}
              onChange={handleChange("country")}
              disabled={readOnly}
            />
          </label>

          <label className="admin-form-grid__full">
            Coverage area
            <input
              type="text"
              value={form.coverageArea}
              onChange={handleChange("coverageArea")}
              disabled={readOnly}
            />
          </label>

          <label>
            Access method
            <select
              value={form.accessMethod}
              onChange={handleChange("accessMethod")}
              disabled={readOnly}
            >
              {VEHICLE_ACCESS_METHODS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Source status *
            <select
              value={form.sourceStatus}
              onChange={handleChange("sourceStatus")}
              disabled={readOnly}
            >
              {VEHICLE_SOURCE_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Priority
            <input
              type="number"
              min={0}
              value={form.priority}
              onChange={handleChange("priority")}
              disabled={readOnly}
            />
          </label>

          <label className="admin-form-grid__checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={handleChange("isActive")}
              disabled={readOnly}
            />
            Is active (inventory flag only — does not start ingestion)
          </label>

          <label className="admin-form-grid__full">
            Notes
            <textarea
              rows={4}
              value={form.notes}
              onChange={handleChange("notes")}
              disabled={readOnly}
            />
          </label>

          {!readOnly ? (
            <footer className="admin-modal__footer admin-form-grid__full">
              <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </footer>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default VehicleSourceFormModal;
